import type { ContentEncoding } from 'conversational-search-client';

export interface ImageFormat {
    formatName: string;
    fileSize?: string;
}

const BYTES_PER_MEGABYTE = 1024 * 1024;

// The download endpoint resolves a format by name only, so these names must match StockBlocks'
// ImageStockItemDTOFactory::getFormatNameFromEncoding. Small and medium encodings are previews that
// StockBlocks never offers for download, matching the image search page's format selector.
const FORMAT_NAMES_BY_EXTENSION: Readonly<Record<string, string>> = { jpg: 'JPG', eps: 'EPS', png: 'PNG' };
const PREVIEW_SIZES = new Set(['small', 'medium']);

// Megabytes to one decimal, trailing ".0" dropped, as the image search page shows sizes ("0.5 MB", "1 MB").
function formatMegabytes(bytes?: number): string | undefined {
    if (bytes == null || !Number.isFinite(bytes) || bytes <= 0) return undefined;
    return `${Number((bytes / BYTES_PER_MEGABYTE).toFixed(1))} MB`;
}

export function buildImageFormats(encodings: readonly ContentEncoding[]): ImageFormat[] {
    const formatsByName = new Map<string, ImageFormat>();

    for (const { format, size, fileSizeBytes } of encodings) {
        const formatName = FORMAT_NAMES_BY_EXTENSION[format.toLowerCase()];
        const isPreview = size != null && PREVIEW_SIZES.has(size);
        if (!formatName || isPreview || formatsByName.has(formatName)) continue;

        formatsByName.set(formatName, { formatName, fileSize: formatMegabytes(fileSizeBytes) });
    }

    return [...formatsByName.values()];
}
